import { FC, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useParams } from "react-router-dom";

import { CardBlogDiv, PostBlogSection, BlogBackDiv, PostTitle } from "./styles";
import { BLOG_ROUTE } from "utils/constants";
import { IPost, IPostMetadata, IPostRedirect } from "interfaces/interfaces";
import { HeaderSEO } from "components/headerSEO";
import { IconBack } from "../../../../components/Icons/Back";
import { ContentDiv } from "components/containers";
import { NotFound } from "../../../../components/NotFound";
import styled from "styled-components/macro";
import { theme } from "styles/theme";

import { Tag, Space } from "antd";

import Britt from "../../../../assets/images/team/new/Britt.webp";
import Daan from "../../../../assets/images/team/new/Daan.webp";
import Maurits from "../../../../assets/images/team/new/Maurits.webp";
import Pavla from "../../../../assets/images/team/new/Pavla.webp";
import Mykolas from "../../../../assets/images/team/new/Mykolas.webp";
import Joost from "../../../../assets/images/team/new/Joost.webp";
import Vera from "../../../../assets/images/team/new/Vera.webp";
import Vincent from "../../../../assets/images/team/new/Vincent.webp";
import Job from "../../../../assets/images/team/new/Job.webp";
import Flavia from "../../../../assets/images/team/new/Flavia.webp";
import EmptyPhoto from "../../../../assets/images/empty_photo.webp";
import { BlogAPI } from "API/blogApi";
import { deserialize } from "../deserializer";

const BLOG_IMAGES_URL = process.env.REACT_APP_BLOG_IMAGES;

const ImageMap: { [key: string]: string } = {
  Britt: Britt,
  Daan: Daan,
  Maurits: Maurits,
  Pavla: Pavla,
  Joost: Joost,
  Vera: Vera,
  Vincent: Vincent,
  Job: Job,
  Flavia: Flavia,
  Mykolas: Mykolas
};

const AuthDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  font-weight: ${(props) => props.theme.font.weightMedium};
`;

const MoreBox = styled.div`
  padding: 1em;
  margin-top: 3em;
  width: 100%;
  border-radius: ${(props) => props.theme.sizes.borderRadius};
  background-color: ${(props) => props.theme.colors.bgSecondary};
`;

const MoreBoxPosts = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  width: 100%;
  flex-direction: row;
`;

const MoreBoxTitle = styled.h3`
  color: ${(props) => props.theme.colors.text} !important;
  font-weight: ${(props) => props.theme.font.weightMedium} !important;
`;

const Title = styled.span`
  margin: 0.5em 0 0.5em 0 !important;
`;

const Image = styled.img`
  margin-bottom: 0.5em;
`;
const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em; // Provides a small gap between image and text, adjust as needed

  & > div {
    color: ${(props) => props.theme.colors.text};
    display: flex;
    flex-direction: column;
    align-items: flex-start; // Align text to the left

    & > strong {
      display: block;
    }

    & > small {
      display: block;
      font-weight: ${(props) => props.theme.font.weightNormal};
    }
  }
`;

const PostTitleDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 1em; // Provides a small gap between image and text, adjust as needed
`;

const Excerpt = styled.p`
  font-size: 1em !important;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  max-height: 6.5em;
  display: -webkit-box;
  -webkit-line-clamp: 5; /* number of lines to show */
  line-clamp: 5;
  -webkit-box-orient: vertical;
`;

const AuthorImage = styled.img`
  width: 40px; // Adjust the image width as needed
  height: 40px; // Adjust the image height as needed
  border-radius: 50%; // This makes the image rounded
  aspect-ratio: 1/1;
  object-fit: cover;
`;

export const CardBlog: FC<{
  keyPost: string;
  idx: number;
  post: IPostMetadata;
  showExcerpt?: boolean;
}> = ({ keyPost, idx, post, showExcerpt = true }) => {
  const { t, i18n } = useTranslation(`blog`);
  const navigate = useNavigate();

  const AuthorImageSrc = useMemo(
    () => ImageMap[post.authors[0].split(" ")[0]],
    [post]
  );

  return (
    <CardBlogDiv
      onClick={() =>
        navigate(
          `/${i18n.language}${BLOG_ROUTE.slice(5)}/${keyPost}/${
            post.slug[i18n.language as "gb" | "nl"]
          }`
        )
      }
    >
      <Image
        src={BLOG_IMAGES_URL + post.cover_image}
        alt={post.cover_image_caption[i18n.language as "gb" | "nl"]}
        loading={idx > 5 ? "lazy" : "eager"}
      />
      <Space size={[0, "small"]} wrap>
        {post.tags.map((tag) => (
          <Tag
            key={tag}
            bordered={false}
            style={{
              backgroundColor: theme.colors.blogTags,
              color: theme.colors.midGrey,
              borderColor: theme.colors.blogTags,
              borderRadius: "50px"
            }}
          >
            {t(`blog.tags.${tag}`)}
          </Tag>
        ))}
      </Space>
      <Title>{post.title[i18n.language as "gb" | "nl"]}</Title>
      {showExcerpt && (
        <Excerpt>{post.excerpt[i18n.language as "gb" | "nl"]}</Excerpt>
      )}
      <AuthDiv>
        <AuthorInfo>
          {post.authors.length >= 1 && (
            <>
              <AuthorImage
                src={AuthorImageSrc ? AuthorImageSrc : EmptyPhoto}
                alt={post.authors[0]}
              />
              <div>
                <strong>{post.authors[0].split(" ")[0]}</strong>{" "}
                {/* This will make the author's name bold */}
                <small>
                  {post.published_at
                    ? new Date(post.published_at).toLocaleDateString()
                    : ""}
                </small>
              </div>
            </>
          )}
        </AuthorInfo>
      </AuthDiv>
    </CardBlogDiv>
  );
};

const PostBlog: FC = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation("blog");

  const navigate = useNavigate();

  const [similarPosts, setSimilarPosts] = useState<IPostMetadata[]>([]);
  const [post, setPost] = useState<IPost | null>();
  const [loading, setLoading] = useState<boolean>(true);

  // Load similar posts
  useEffect(() => {
    if (post) {
      BlogAPI.list({
        tags: [post.tags[0]],
        amount: 4
      }).then((result) => {
        if (result) {
          setSimilarPosts(
            result.data.filter((x) => x.post_id !== post.post_id).slice(0, 3)
          );
        }
      });
    }
  }, [post]);

  // Fetch the blog post or redirect if old!
  useEffect(() => {
    setLoading(true);
    BlogAPI.get(id).then((fetchedPost) => {
      if (fetchedPost && "redirect" in fetchedPost) {
        const redirect = fetchedPost as IPostRedirect;
        if (redirect.redirect) {
          navigate(
            `/${i18n.language}${BLOG_ROUTE.slice(5)}/${redirect.post_id}/${
              redirect.slug
            }`
          );
        }
      } else if (fetchedPost) {
        setPost(fetchedPost as IPost);
      }
      setLoading(false);
    });
  }, [id]);

  // Update slug when language changes
  useEffect(() => {
    if (post) {
      navigate(
        `/${i18n.language}${BLOG_ROUTE.slice(5)}/${id}/${
          post.slug[i18n.language as "gb" | "nl"]
        }`,
        { replace: true }
      );
    }
  }, [i18n.language]);

  const AuthorImageSrc = useMemo(
    () => post && ImageMap[post.authors[0].split(" ")[0]],
    [post]
  );

  if (!post && !loading) {
    return <NotFound />;
  } else if (!post && loading) {
    return <ContentDiv></ContentDiv>;
  }

  return (
    <ContentDiv>
      <HeaderSEO
        title={post?.title[i18n.language as "gb" | "nl"]}
        description={post?.excerpt[i18n.language as "gb" | "nl"]}
        keywords={post?.keywords ? post?.keywords.join(", ") : ""}
        canonicallink={window.location.href}
      />
      <PostBlogSection>
        <BlogBackDiv>
          <NavLink to={`/${i18n.language}${BLOG_ROUTE.slice(5)}`}>
            <>
              {IconBack}
              {t("blog.backToAll")}
            </>
          </NavLink>
        </BlogBackDiv>
        <PostTitle>{post?.title[i18n.language as "gb" | "nl"]}</PostTitle>
        <div>
          <Image src={post ? BLOG_IMAGES_URL + post?.cover_image : ""} alt="" />
          <span>{post?.cover_image_caption[i18n.language as "gb" | "nl"]}</span>
        </div>
        <PostTitleDiv>
          <AuthorInfo>
            {post && post.authors.length >= 1 && (
              <>
                <AuthorImage
                  src={AuthorImageSrc ? AuthorImageSrc : EmptyPhoto}
                  alt={post.authors[0]}
                />
                <div>
                  <strong>{post.authors[0].split(" ")[0]}</strong>{" "}
                  <small>
                    {post.published_at
                      ? new Date(post.published_at).toLocaleDateString()
                      : ""}
                  </small>
                </div>
              </>
            )}
          </AuthorInfo>
          <Space size={[0, "small"]} wrap>
            {post &&
              post.tags.map((tag) => (
                <Tag
                  key={tag}
                  bordered={false}
                  style={{
                    backgroundColor: theme.colors.blogTags,
                    color: theme.colors.midGrey,
                    borderColor: theme.colors.blogTags,
                    borderRadius: "50px"
                  }}
                >
                  {t(`blog.tags.${tag}`)}
                </Tag>
              ))}
          </Space>
        </PostTitleDiv>
        {deserialize(post?.body[i18n.language as "gb" | "nl"])}
        <MoreBox>
          <MoreBoxTitle>{t("blog.similarPosts")}</MoreBoxTitle>
          <MoreBoxPosts>
            {similarPosts.map((post, index) => (
              <CardBlog
                keyPost={post.post_id}
                idx={index}
                key={post.post_id}
                post={post}
                showExcerpt={false}
              />
            ))}
          </MoreBoxPosts>
        </MoreBox>
      </PostBlogSection>
    </ContentDiv>
  );
};

export default PostBlog;
